import React, { useCallback, useEffect, useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  selectSelectedCompany,
  setSelectedCompany,
} from "../../../redux-toolkit/features/auth/authSlice";
import { useGetAdminManagedCompaniesQuery } from "../../../redux-toolkit/features/rbac/rbacApiSlice";
import { apiSlice } from "../../../redux-toolkit/api/apiSlice";
import useAuth from "../../../hooks/useAuth";
import { getCompanyContextRedirect } from "../../../utils/companyContextNavigation";
import "../../UI/Delete Modal/DeleteModal.css";

const confirmModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxWidth: "90vw",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "30px",
};

export const CompanySwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCompany = useSelector(selectSelectedCompany);
  const { id, company, managedCompanies } = useAuth();
  const { data: adminCompaniesData, isLoading: isLoadingCompanies } =
    useGetAdminManagedCompaniesQuery(id, {
      skip: !id,
    });
  const [companyMap, setCompanyMap] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCompanyId, setPendingCompanyId] = useState(null);

  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof id === "string") return id;
    if (id && typeof id.toString === "function") return id.toString();
    return String(id);
  };

  const currentCompanyId = normalizeId(
    selectedCompany || company || "",
  );

  useEffect(() => {
    if (adminCompaniesData?.data) {
      const map = {};

      if (adminCompaniesData.data.primaryCompany) {
        const primaryCompany = adminCompaniesData.data.primaryCompany;
        const primaryId = normalizeId(primaryCompany._id);
        if (primaryId && primaryCompany.name) {
          map[primaryId] = primaryCompany.name;
        }
      }

      if (adminCompaniesData.data.managedCompanies) {
        adminCompaniesData.data.managedCompanies.forEach((c) => {
          const normalizedId = normalizeId(c._id);
          if (normalizedId && c.name) {
            map[normalizedId] = c.name;
          }
        });
      }

      setCompanyMap(map);
    }
  }, [adminCompaniesData]);

  useEffect(() => {
    const savedCompany = localStorage.getItem("selectedCompany");
    if (savedCompany) {
      const accessibleCompanies = company
        ? [company, ...(managedCompanies || [])]
        : managedCompanies || [];
      const accessibleCompanyIds = accessibleCompanies
        .filter((id) => id)
        .map((id) => id?.toString() || id);
      if (accessibleCompanyIds.includes(savedCompany)) {
        dispatch(setSelectedCompany(savedCompany));
        return;
      }
    }

    if (managedCompanies && managedCompanies.length > 0 && !selectedCompany) {
      const defaultCompany = company || managedCompanies[0];
      if (defaultCompany) {
        dispatch(setSelectedCompany(defaultCompany));
        localStorage.setItem("selectedCompany", defaultCompany);
      }
    }
  }, [company, managedCompanies, dispatch, selectedCompany]);

  const getCompanyName = useCallback(
    (companyId) => {
      if (!companyId) return "Unknown Company";

      const normalizedId = normalizeId(companyId);
      let name = companyMap[normalizedId];

      if (!name && adminCompaniesData?.data) {
        if (adminCompaniesData.data.primaryCompany) {
          const primaryId = normalizeId(
            adminCompaniesData.data.primaryCompany._id,
          );
          if (primaryId === normalizedId) {
            name = adminCompaniesData.data.primaryCompany.name;
          }
        }

        if (!name && adminCompaniesData.data.managedCompanies) {
          const foundCompany = adminCompaniesData.data.managedCompanies.find(
            (c) => normalizeId(c._id) === normalizedId,
          );
          if (foundCompany) {
            name = foundCompany.name;
          }
        }
      }

      if (!name) {
        if (isLoadingCompanies) {
          return "Loading...";
        }
        return normalizedId || "Unknown Company";
      }

      return name;
    },
    [adminCompaniesData, companyMap, isLoadingCompanies],
  );

  const applyCompanySwitch = useCallback(
    (newCompanyId) => {
      dispatch(setSelectedCompany(newCompanyId));
      localStorage.setItem("selectedCompany", newCompanyId);

      dispatch(
        apiSlice.util.invalidateTags([
          "Books",
          "BookById",
          "Company",
          "Admin",
          "Permission",
          "Course",
          "CourseDetail",
          "TimeTrails",
          "AdminTimeTrialSubmissions",
          "Points",
          "UserActivity",
          "AdminReports",
        ]),
      );

      const target = getCompanyContextRedirect(
        location.pathname,
        newCompanyId,
      );
      if (target) {
        navigate(target, { replace: true });
      }
    },
    [dispatch, location.pathname, navigate],
  );

  const handleCompanyChange = (event) => {
    const newCompanyId = normalizeId(event.target.value);
    if (!newCompanyId || newCompanyId === currentCompanyId) {
      return;
    }
    setPendingCompanyId(newCompanyId);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setPendingCompanyId(null);
  };

  const handleConfirmSwitch = () => {
    if (pendingCompanyId) {
      applyCompanySwitch(pendingCompanyId);
    }
    handleConfirmClose();
  };

  const hasMultipleCompanies =
    (managedCompanies && managedCompanies.length > 0 && company) ||
    (managedCompanies && managedCompanies.length > 1);

  const renderSelectValue = (value) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        component="span"
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color: "var(--grey600)",
          backgroundColor: "var(--grey200)",
          px: 1,
          py: 0.25,
          borderRadius: "4px",
        }}
      >
        Company
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: "14px",
          color: value ? "var(--grey600)" : "var(--grey400)",
          fontWeight: value ? 500 : 400,
        }}
      >
        {value ? getCompanyName(value) : "Select company"}
      </Typography>
    </Box>
  );

  if (!hasMultipleCompanies) {
    return null;
  }

  if (isLoadingCompanies && Object.keys(companyMap).length === 0) {
    return (
      <Box sx={{ minWidth: 280, mx: 2 }}>
        <FormControl fullWidth size="small">
          <Select
            id="company-switcher"
            value=""
            notched={false}
            displayEmpty
            disabled={true}
            renderValue={() => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "var(--grey600)",
                    backgroundColor: "var(--grey200)",
                    px: 1,
                    py: 0.25,
                    borderRadius: "4px",
                  }}
                >
                  Company
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontSize: "14px", color: "var(--grey400)" }}
                >
                  Loading companies...
                </Typography>
              </Box>
            )}
            sx={{
              backgroundColor: "var(--pwhite)",
              borderRadius: "5px",
              fontSize: "14px",
            }}
          >
            <MenuItem value="" disabled>
              <Typography
                variant="body2"
                sx={{ fontSize: "14px", color: "var(--grey400)" }}
              >
                Loading companies...
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minWidth: 280,
          mx: 2,
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <FormControl fullWidth size="small">
          <Select
            id="company-switcher"
            notched={false}
            value={currentCompanyId}
            displayEmpty
            variant="standard"
            disableUnderline
            renderValue={renderSelectValue}
            onChange={handleCompanyChange}
            sx={{
              backgroundColor: "var(--pwhite)",
              borderRadius: "5px",
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--grey600)",
              "&.Mui-focused": {
                borderRadius: "5px",
              },
              "& .MuiSelect-select:focus": {
                backgroundColor: "var(--pwhite)",
              },
              "& .MuiSelect-select.Mui-focusVisible": {
                backgroundColor: "var(--pwhite)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--grey300)",
                borderWidth: "0.5px",
                borderRadius: "5px",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--pblue)",
                borderWidth: "1px",
                borderRadius: "5px",
              },
              "& .MuiSelect-icon": {
                color: "var(--grey400)",
              },
              "&:hover .MuiSelect-icon": {
                color: "var(--grey600)",
              },
              "&.Mui-focused .MuiSelect-icon": {
                color: "var(--pblue)",
              },
              "& .MuiOutlinedInput-input": {
                padding: "9px 14px",
              },
              "& .MuiSelect-select": {
                padding: "9px 14px !important",
                display: "flex",
                alignItems: "center",
                borderRadius: "5px",
              },
              "& .MuiInputBase-root": {
                height: "36px",
                borderRadius: "5px",
              },
              "& .MuiOutlinedInput-notchedOutline legend": {
                display: "none",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "5px",
                  marginTop: "4px",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "10px 16px",
                    color: "var(--grey600)",
                    "&:hover": {
                      backgroundColor: "var(--slightpurple)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "var(--slightpurple)",
                      color: "var(--pblue)",
                      fontWeight: 600,
                    },
                  },
                },
              },
            }}
          >
            {company && (
              <MenuItem value={normalizeId(company)}>
                <Typography variant="body2" sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {getCompanyName(company)}{" "}
                  <span style={{ color: "inherit", fontWeight: 400 }}>
                    (Primary)
                  </span>
                </Typography>
              </MenuItem>
            )}
            {managedCompanies.map((companyId) => {
              const normalizedId = normalizeId(companyId);
              return (
                <MenuItem key={normalizedId} value={normalizedId}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: 500 }}
                  >
                    {getCompanyName(companyId)}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      <Modal
        open={confirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="company-switch-confirm-title"
        aria-describedby="company-switch-confirm-description"
      >
        <Box sx={confirmModalStyle}>
          <div className="deletemodal">
            <div className="deletemodal__header">
              <IconButton
                onClick={handleConfirmClose}
                sx={{
                  position: "absolute",
                  right: -24,
                  top: -24,
                  color: "#444444",
                }}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div className="deletemodal__content">
              <h3 className="deletemodal__title" id="company-switch-confirm-title">
                Switch company?
              </h3>
              <p
                className="deletemodal__question"
                id="company-switch-confirm-description"
                style={{ fontSize: "14px", fontWeight: 400, lineHeight: 1.5 }}
              >
                Unsaved changes on this page will be lost. If you are editing or
                creating content, you will be returned to the listing for the
                selected company
                {pendingCompanyId
                  ? ` (${getCompanyName(pendingCompanyId)}).`
                  : "."}
              </p>
            </div>
            <div className="deletemodal__action">
              <Button
                onClick={handleConfirmClose}
                variant="outlined"
                sx={{
                  width: "120px",
                  color: "grey.600",
                  borderColor: "grey.300",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSwitch}
                variant="contained"
                sx={{
                  width: "140px",
                  backgroundColor: "#3361cf",
                  "&:hover": { backgroundColor: "#2a4f9e" },
                }}
              >
                Switch company
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};
